import { useIsMobileActive } from '../../hooks/useIsMobileActive';
import { mobileDestinationMessage, resolveMobileDestination } from '../mobile/navigation';
import React from 'react';
import { CalendarCheck, CalendarDays, CheckSquare, FileSpreadsheet, FileText, FolderKanban, Handshake, Receipt, Search, UserCircle2, Wrench } from 'lucide-react';
import { useMobileNavigate } from '../mobile/MobileNavigationGuard';

import { GLOBAL_SEARCH_MIN_QUERY_LENGTH, useGlobalSearch } from '../../hooks/useGlobalSearch';
import type { GlobalSearchGroup, GlobalSearchResult } from '../../types/search';
import { cn } from '../../lib/utils';
import { useModalState } from '../../hooks/useModalState';
import { useApiBase } from '../../hooks/useApiBase';
import { authFetch } from '../../lib/authFetch';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Badge } from '../ui/badge';

const iconMap: Record<string, React.ReactNode> = {
  project: <FolderKanban className="h-4 w-4 text-primary" aria-hidden />,
  deal: <Handshake className="h-4 w-4 text-primary" aria-hidden />,
  contact: <UserCircle2 className="h-4 w-4 text-primary" aria-hidden />,
  task: <CheckSquare className="h-4 w-4 text-primary" aria-hidden />,
  calendar_event: <CalendarDays className="h-4 w-4 text-primary" aria-hidden />,
  estimate: <FileSpreadsheet className="h-4 w-4 text-primary" aria-hidden />,
  invoice: <Receipt className="h-4 w-4 text-primary" aria-hidden />,
  tradeshow: <CalendarCheck className="h-4 w-4 text-primary" aria-hidden />,
  service_order: <Wrench className="h-4 w-4 text-primary" aria-hidden />,
  file: <FileText className="h-4 w-4 text-primary" aria-hidden />,
};

function renderResultIcon(result: GlobalSearchResult) {
  return iconMap[result.icon_key ?? result.entity_type] ?? <Search className="h-4 w-4 text-primary" aria-hidden />;
}

function isTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea') {
    return true;
  }
  return target.isContentEditable;
}

type GlobalSearchLauncherProps = {
  className?: string;
};

export function buildGlobalSearchFileDownloadUrl(apiBase: string, result: GlobalSearchResult): string {
  const normalizedBase = apiBase ? apiBase.replace(/\/$/, '') : '';
  const contextParams = new URLSearchParams();
  const metadata = result.metadata && typeof result.metadata === 'object' ? result.metadata : null;
  const context = metadata && metadata.context && typeof metadata.context === 'object'
    ? (metadata.context as Record<string, unknown>)
    : null;
  if (context) {
    Object.entries(context).forEach(([key, value]) => {
      if (value == null) {
        return;
      }
      const stringValue = String(value).trim();
      if (stringValue) {
        contextParams.append(`context.${key}`, stringValue);
      }
    });
  }
  if (metadata && typeof metadata.scope === 'string') {
    const scope = metadata.scope.trim();
    if (scope) {
      contextParams.append('scope', scope);
    }
  }
  contextParams.append('disposition', 'inline');
  const query = contextParams.toString();
  return `${normalizedBase}/api/files/${result.id}/download-url${query ? `?${query}` : ''}`;
}

export function GlobalSearchLauncher({ className }: GlobalSearchLauncherProps) {
  const titleId = React.useId();
  const navigate = useMobileNavigate();
  const mobile = useIsMobileActive();
  const modal = useModalState();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const apiBase = useApiBase();
  const { query, setQuery, groups, isSearching, error, reset } = useGlobalSearch();
  const [fileError, setFileError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!modal.isOpen) {
      reset();
      setQuery('');
      return;
    }
    if (mobile) return;
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [mobile, modal.isOpen, reset, setQuery]);

  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === 'k') {
        event.preventDefault();
        modal.open();
        return;
      }
      if (!event.metaKey && !event.ctrlKey && !event.altKey && key === '/' && !isTextInput(event.target)) {
        event.preventDefault();
        modal.open();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modal]);

  const openFileResult = React.useCallback(
    async (result: GlobalSearchResult) => {
      const endpoint = buildGlobalSearchFileDownloadUrl(apiBase, result);
      setFileError(null);
      try {
        const response = await authFetch(endpoint);
        const payload = await response.json();
        if (!response.ok || !payload?.url) {
          throw new Error(payload?.error || 'Preview unavailable');
        }
        window.open(payload.url, '_blank', 'noopener,noreferrer');
        modal.close();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to open file';
        setFileError(message);
      }
    },
    [apiBase, modal]
  );

  const handleSelect = React.useCallback(
    (result: GlobalSearchResult) => {
      if (result.entity_type === 'file') {
        void openFileResult(result);
        return;
      }
      if (mobile && result.url && resolveMobileDestination(result.url) !== 'supported') { setFileError(mobileDestinationMessage(result.url)); return; }
      setFileError(null);
      if (result.url) {
        navigate(result.url);
      }
      modal.close();
    },
    [modal, navigate, openFileResult, mobile]
  );

  const combinedError = error || fileError;
  const trimmedQuery = query.trim();
  const isBelowMinimum = trimmedQuery.length > 0 && trimmedQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH;
  const hasNoResults = groups.every((group) => group.results.length === 0);

  return (
      <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              'rounded-full text-muted-foreground hover:text-foreground',
              modal.isOpen && 'bg-primary/10 text-primary',
              className
            )}
            onClick={modal.open}
            aria-label="Open global search"
          >
            <Search className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="center">
          Search everything · Ctrl/⌘K
        </TooltipContent>
      </Tooltip>

      <Dialog open={modal.isOpen} onOpenChange={modal.setOpen}>
        <DialogContent className="p-0 sm:max-w-2xl" aria-labelledby={titleId}>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <DialogTitle id={titleId} className="text-sm font-semibold text-foreground">Global search</DialogTitle>
              <p className="text-xs text-muted-foreground">Find records, addresses, invoices, and files</p>
            </div>
          </div>
          <Command shouldFilter={false} className="border-0">
            <CommandInput
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder="Search names, IDs, street addresses, files…"
              autoFocus={!mobile}
            />
            <CommandList>
              {(!trimmedQuery || isBelowMinimum || (!isSearching && hasNoResults)) && (
                <div className="py-6 text-center text-sm text-muted-foreground" role="status">
                  {!trimmedQuery
                    ? 'Start typing to search records.'
                    : isBelowMinimum
                      ? 'Enter at least 2 characters.'
                      : 'No results yet. Try another keyword.'}
                </div>
              )}
              {groups.map((group) => (
                <GlobalSearchResultGroup key={group.key} group={group} onSelect={handleSelect} />
              ))}
            </CommandList>
            <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
              {isSearching ? <span>Searching…</span> : <span>Tip: Use Ctrl/⌘K or / to open quickly.</span>}
              {combinedError ? <span className="text-destructive">{combinedError}</span> : null}
            </div>
          </Command>
        </DialogContent>
      </Dialog>
      </>
  );
}

function GlobalSearchResultGroup({
  group,
  onSelect,
}: {
  group: GlobalSearchGroup;
  onSelect: (result: GlobalSearchResult) => void;
}) {
  const mobile = useIsMobileActive();
  if (!group.results.length) {
    return null;
  }

  return (
    <CommandGroup
      heading={group.label}
      className="px-2 py-1"
    >
      {group.results.map((result) => (
        <CommandItem
          key={`${group.key}-${result.id}`}
          value={`${group.key}-${result.id}`}
          onSelect={() => onSelect(result)}
          className="flex min-h-11 items-center gap-3 px-3 py-2"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-foreground">
            {renderResultIcon(result)}
          </div>
          <div className="flex flex-1 flex-col text-left">
            <span className="text-sm font-medium text-foreground">{result.title}</span>
            {mobile && result.entity_type !== 'file' && result.url && resolveMobileDestination(result.url) !== 'supported' ? <span className="text-xs text-muted-foreground">{resolveMobileDestination(result.url) === 'desktop' ? 'Desktop only' : 'Unavailable destination'}</span> : null}
            {result.subtitle ? <span className="text-xs text-muted-foreground">{result.subtitle}</span> : null}
          </div>
          {result.badge ? (
            <Badge variant="outline" className="text-[11px]">
              {result.badge}
            </Badge>
          ) : null}
        </CommandItem>
      ))}
      {group.has_more ? (
        <div className="px-3 pb-2 text-xs text-muted-foreground">Showing top results</div>
      ) : null}
    </CommandGroup>
  );
}
